/**
 * Deterministic per-location facts (paragraph + JSON-LD) layered ON TOP of
 * the existing variant-specific pSEO copy. Goal: make every location page
 * carry unique numerics so Google does not omit them as duplicate.
 */

import {
  type Location,
  getProvinsi,
  getKecamatanInKota,
  tzLabel,
  KOTA_ALL,
} from "@/data/locations";
import { displayLocationTitle } from "./copy";
import type { FaqItem } from "@/lib/calendar/semantics";

/** Haversine distance in km. */
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function bearingToCardinal(from: { lat: number; lng: number }, to: { lat: number; lng: number }): string {
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const brng = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  const dirs = ["utara", "timur laut", "timur", "tenggara", "selatan", "barat daya", "barat", "barat laut"];
  return dirs[Math.round(brng / 45) % 8];
}

export interface LocationFactsBlock {
  paragraph: string;
  facts: { label: string; value: string }[];
  faq: FaqItem[];
}

export function buildLocationFacts(variant: "sholat" | "imsak" | "buka-puasa", loc: Location): LocationFactsBlock {
  const display = displayLocationTitle(loc);
  const prov = getProvinsi(loc.provinceSlug);
  const tz = tzLabel(loc.tz);
  const facts: { label: string; value: string }[] = [
    { label: "Koordinat", value: `${loc.lat.toFixed(4)}° / ${loc.lng.toFixed(4)}°` },
    { label: "Zona waktu", value: `${loc.tz} (${tz})` },
    { label: "Metode hisab", value: "Kemenag RI (Subuh 20°, Isya 18°, Asar Syafi'i, ihtiyat 2′)" },
  ];

  let geoSentence = "";
  if (prov && (loc.kind === "kota" || loc.kind === "kecamatan")) {
    const distKm = haversineKm(loc, prov);
    const dir = bearingToCardinal(prov, loc);
    facts.push({ label: `Jarak ke ${prov.name}`, value: `${distKm.toFixed(0)} km ${dir}` });
    geoSentence = `${display} berjarak sekitar ${distKm.toFixed(0)} km arah ${dir} dari ibu kota provinsi ${prov.name}.`;
  }

  let kecCount = 0;
  if (loc.kind === "kota") {
    kecCount = getKecamatanInKota(loc.slug).length;
    if (kecCount > 0) facts.push({ label: "Jumlah kecamatan terdata", value: `${kecCount}` });
  }
  if (loc.kind === "provinsi") {
    const kotas = KOTA_ALL.filter((k) => k.provinceSlug === loc.slug);
    facts.push({ label: "Jumlah kota/kabupaten terdata", value: `${kotas.length}` });
  }

  const variantNoun = variant === "sholat" ? "jadwal sholat" : variant === "imsak" ? "jadwal imsakiyah" : "jadwal buka puasa";
  const methodSentence = variant === "buka-puasa"
    ? " Perhitungan menonjolkan waktu Maghrib sebagai waktu iftar/buka puasa, dengan Imsak dan Subuh sebagai konteks sahur serta Isya untuk Tarawih."
    : "";
  const paragraph =
    `Halaman ini menampilkan ${variantNoun} untuk ${display} berdasarkan koordinat ` +
    `${loc.lat.toFixed(4)}° LS / ${loc.lng.toFixed(4)}° BT pada zona waktu ${loc.tz} (${tz}). ` +
    geoSentence +
    (kecCount > 0
      ? ` Wilayah ini memiliki ${kecCount} kecamatan terdata yang masing-masing punya halaman jadwal sendiri di bawah.`
      : "") +
    ` Perhitungan menggunakan metode resmi Kementerian Agama RI sehingga selisih dengan jadwal SIHAT Kemenag tidak lebih dari satu menit.` +
    methodSentence;

  const faq: FaqItem[] = [
    {
      q: `Berapa koordinat yang dipakai untuk jadwal ${display}?`,
      a: `Latitude ${loc.lat.toFixed(4)}° dan longitude ${loc.lng.toFixed(4)}°, zona waktu ${loc.tz} (${tz}).`,
    },
    {
      q: `Apa zona waktu ${display}?`,
      a: `${display} berada di zona ${tz} (${loc.tz}, UTC${loc.tz === "WIB" ? "+7" : loc.tz === "WITA" ? "+8" : "+9"}).`,
    },
  ];

  return { paragraph, facts, faq };
}

/** schema.org Place node for entity surface. */
export function locationPlaceJsonLd(loc: Location): Record<string, unknown> {
  const display = displayLocationTitle(loc);
  return {
    "@context": "https://schema.org",
    "@type": "Place",
    name: display,
    geo: {
      "@type": "GeoCoordinates",
      latitude: loc.lat,
      longitude: loc.lng,
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "ID",
      addressRegion: getProvinsi(loc.provinceSlug)?.name ?? undefined,
    },
  };
}
